/*
 * icon : success | warn 图标
 * duration : 持续时间 默认2000ms
 * 移动优先设计，适配Svelte 5框架
 * 注意内存开销和兼容性
 */

import "./toast.css";
import success from "./assets/success.png";
import warn from "./assets/warn.png";

const icons = {
  success,
  warn,
};

// 存储当前活动的toast元素
let activeToasts = [];

// 存储toast定时器映射 - 修复定时器管理问题
const toastTimers = new WeakMap();

// DOM复用池 - 重用toast元素以减少DOM操作
const toastPool = [];
const MAX_POOL_SIZE = 5;

// 防抖控制 - 防止短时间内重复显示相同内容
let debounceTimers = {};
const DEBOUNCE_TIME = 300; // 毫秒

// 检查是否在浏览器环境中
const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";

// 单例容器 - 所有toast都添加到这个容器中
let toastContainer = null;

// 创建HTML模板 - 只创建一次结构模板
let toastTemplate;

// 缓存常用DOM查询，减少选择器操作
const elementCache = new WeakMap();

// 保存各位置toast的数量
const positionCounts = {
  top: 0,
  middle: 0,
  bottom: 0,
};

// toast垂直间距
const TOAST_VERTICAL_SPACING = 16;

// 保存位置映射关系
const positionMap = new WeakMap();

// 简单函数处理toast样式
function fixToastStyles() {
  if (!isBrowser || !toastContainer) return;

  // 修改主容器样式
  toastContainer.style.width = "100%";

  // 修改位置容器样式，使用高效的选择器
  const containers = {
    top: toastContainer.querySelector(".toast--position-top"),
    middle: toastContainer.querySelector(".toast--position-middle"),
    bottom: toastContainer.querySelector(".toast--position-bottom"),
  };

  // 共用样式设置 - 减少代码重复
  Object.values(containers).forEach((container) => {
    if (!container) return;

    // 批量设置样式，减少重排
    Object.assign(container.style, {
      position: "absolute",
      left: "0",
      right: "0",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "10px",
    });
  });

  // 设置特定位置的样式
  if (containers.top) {
    containers.top.style.top = "10px";
  }

  if (containers.middle) {
    containers.middle.style.top = "50%";
    containers.middle.style.transform = "translateY(-50%)";
  }

  if (containers.bottom) {
    containers.bottom.style.bottom = "10px";
  }
}

const getUI = () => {
  const theme = import.meta.env.VITE_THEME
  let d = {}
  if(theme === 'blue') {
    d = {
      bg: '#4C9EEA',
    }
  } else if(theme === 'orange') {
    d = {
      bg: '#FFBB5C',
    }
  } else if(theme === 'gold') {
    d = {
      bg: '#ECC177',
    }
  } else if(theme === 'purple') {
    d = {
      bg: '#716AC7',
    }
  }
  return d
}

// 初始化模板和容器
const init = () => {
  if (!isBrowser || (toastTemplate && toastContainer)) return;
  let ui = getUI()
  // 初始化模板 - 使用轻量级HTML结构
  if (!toastTemplate) {
    toastTemplate = document.createElement("template");
    toastTemplate.innerHTML = `
      <div class="toast--box" role="alert" aria-live="polite" style="background: ${ui.bg};">
        <div class="toast--con">
          <div class="toast--icon" style="display:none">
            <img width="20" height="20" alt="" decoding="async" loading="lazy">
          </div>
          <div class="toast--msg"></div>
        </div>
      </div>
    `;
  }

  // 初始化容器
  if (!toastContainer) {
    toastContainer = document.createElement("div");
    toastContainer.className = "toast--container"; 
    toastContainer.setAttribute("aria-live", "polite");
    toastContainer.setAttribute("aria-atomic", "true");

    // 使用transform:translateZ(0)创建层叠上下文，启用GPU加速
    Object.assign(toastContainer.style, {
      position: "fixed",
      zIndex: "9999",
      pointerEvents: "none",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      transform: "translateZ(0)",
    });

    // 创建三个位置容器
    ["top", "middle", "bottom"].forEach((pos) => {
      const posContainer = document.createElement("div");
      posContainer.className = `toast--position-${pos}`;

      // 设置基础样式
      Object.assign(posContainer.style, {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      });

      // 根据位置设置特定样式
      if (pos === "top") {
        posContainer.style.paddingTop = "10px";
        posContainer.style.justifyContent = "flex-start";
      } else if (pos === "middle") {
        posContainer.style.flex = "1";
        posContainer.style.justifyContent = "center";
        posContainer.style.gap = `${TOAST_VERTICAL_SPACING}px`;
      } else {
        posContainer.style.paddingBottom = "10px";
        posContainer.style.justifyContent = "flex-end";
      }

      toastContainer.appendChild(posContainer);
    });

    // 高效添加到DOM
    if (document.body) {
      document.body.appendChild(toastContainer);
      // 应用样式修复
      requestAnimationFrame(fixToastStyles);
    } else {
      // 如果body不存在，等待DOM就绪
      window.addEventListener(
        "DOMContentLoaded",
        () => {
          document.body.appendChild(toastContainer);
          requestAnimationFrame(fixToastStyles);
        },
        { once: true },
      );
    }
  }
};

// 获取元素缓存 - 减少DOM查询
const getElementCache = (toast) => {
  if (!elementCache.has(toast)) {
    elementCache.set(toast, {
      messageElement: toast.querySelector(".toast--msg"),
      iconContainer: toast.querySelector(".toast--icon"),
      iconImage: toast.querySelector(".toast--icon img"),
    });
  }
  return elementCache.get(toast);
};

// 在初始化函数结束后添加样式更新
const enhancedInit = () => {
  init();
  // 等待DOM更新后应用样式
  if (isBrowser) {
    requestAnimationFrame(fixToastStyles);
  }
};

// 在初始化时使用增强版init函数
if (isBrowser) {
  // 使用 requestIdleCallback 在浏览器空闲时初始化，不阻塞关键渲染路径
  if (window.requestIdleCallback) {
    window.requestIdleCallback(enhancedInit);
  } else {
    setTimeout(enhancedInit, 300);
  }
}

/**
 * 获取指定位置的容器
 * @param {string} position - 位置：'top'|'middle'|'bottom'
 * @returns {HTMLElement} - 对应位置的容器
 */
const getPositionContainer = (position) => {
  if (!toastContainer) return null;
  return toastContainer.querySelector(`.toast--position-${position}`);
};

/**
 * 优化更新toast元素位置，减少重排
 * @param {string} position - 位置
 */
const updateToastPositions = (position) => {
  const container = getPositionContainer(position);
  if (!container || !container.children.length) return;

  // 批量操作，减少重排
  requestAnimationFrame(() => {
    // 使用性能更高的实现方式
    const toasts = Array.from(container.children);

    // 为了提高性能，直接设置样式而不是添加类名
    if (position === "middle") {
      // 清除所有转换
      toasts.forEach((toast) => {
        toast.style.transform = "";
      });
    } else if (position === "top") {
      // 顶部区域
      toasts.forEach((toast, index) => {
        toast.style.marginTop = index > 0 ? `${TOAST_VERTICAL_SPACING}px` : "0";
      });
    } else {
      // 底部区域
      toasts.forEach((toast, index) => {
        toast.style.marginBottom = index > 0 ? `${TOAST_VERTICAL_SPACING}px` : "0";
      });
    }
  });
};

/**
 * 从池中获取或创建Toast元素
 * @param {string} content - 显示的内容
 * @param {string} icon - 图标类型
 * @param {string} position - 位置：'top'|'middle'|'bottom'，默认为'middle'
 * @returns {HTMLElement} - 创建的Toast元素
 */
const createToastElement = (content, icon, position = "middle") => {
  if (!isBrowser) return null;

  // 确保初始化
  if (!toastTemplate || !toastContainer) init();

  let toast;
  let isFromPool = false;

  // 确保位置是有效的，默认为middle
  position = ["top", "middle", "bottom"].includes(position) ? position : "middle";

  // 增加该位置的toast计数
  positionCounts[position]++;

  // 尝试从池中获取元素
  if (toastPool.length > 0) {
    toast = toastPool.pop();
    isFromPool = true;

    // 确保清理之前的定时器
    const existingTimer = toastTimers.get(toast);
    if (existingTimer) {
      clearTimeout(existingTimer);
      toastTimers.delete(toast);
    }

    // 重置从池中获取的元素样式 (使用批量样式设置减少重排)
    Object.assign(toast.style, {
      opacity: "0",
      transform: "",
      marginTop: "",
      marginBottom: "",
      zIndex: "",
      visibility: "hidden",
    });

    // 确保移除旧的动画类，同时删除内联动画样式
    toast.classList.remove("toast--show", "toast--hide");
    toast.style.animation = "none";

    // 触发一次重排以应用animation:none
    void toast.offsetWidth;
  } else {
    // 创建新元素 - 使用模板克隆以提高性能
    toast = toastTemplate.content.firstElementChild.cloneNode(true);

    // 使用事件委托减少事件监听器数量
    toast.addEventListener("click", hideToastElement.bind(null, toast), { passive: true });
    toast.addEventListener(
      "touchstart",
      (e) => {
        e.stopPropagation();
        hideToastElement(toast);
      },
      { passive: true },
    );
  }

  // 记录toast的位置
  positionMap.set(toast, position);

  // 获取缓存的DOM元素引用
  const { messageElement, iconContainer, iconImage } = getElementCache(toast);

  // 设置内容
  messageElement.textContent = content;

  // 设置图标
  if (icon && icons[icon]) {
    iconContainer.style.display = "";
    // 只有在图片源变化时才更新
    if (iconImage.src !== icons[icon]) {
      iconImage.src = icons[icon];
      iconImage.alt = icon;
    }
  } else {
    iconContainer.style.display = "none";
  }

  // 设置类名
  toast.className = "toast--box";

  // 预先设置所有样式
  Object.assign(toast.style, {
    position: "relative",
    pointerEvents: "auto",
    maxWidth: "80%",
    width: "auto",
    marginLeft: "auto",
    marginRight: "auto",
    zIndex: 9999 + positionCounts[position],
    visibility: "hidden",
  });

  // 使用requestAnimationFrame添加到DOM，避免渲染阻塞
  requestAnimationFrame(() => {
    const posContainer = getPositionContainer(position);
    if (posContainer) {
      // 根据位置决定添加顺序
      if (position === "bottom") {
        // 底部位置新toast添加到顶部
        if (posContainer.firstChild) {
          posContainer.insertBefore(toast, posContainer.firstChild);
        } else {
          posContainer.appendChild(toast);
        }
      } else {
        // 顶部和中部位置新toast添加到底部
        posContainer.appendChild(toast);
      }

      // 更新toast位置
      updateToastPositions(position);

      // 显示toast - 确保清除内联样式以便动画效果生效
      requestAnimationFrame(() => {
        // 确保动画状态正确重置
        toast.classList.remove("toast--show", "toast--hide");

        // 清除可能阻止动画的内联样式
        toast.style.animation = "";
        toast.style.visibility = "";

        // 强制重排以确保动画状态正确
        void toast.offsetWidth;

        // 触发动画
        toast.classList.add("toast--show");
      });
    }
  });

  return toast;
};

/**
 * 隐藏Toast元素(通过CSS动画)
 * @param {HTMLElement} toast - 要隐藏的Toast元素
 */
const hideToastElement = (toast) => {
  if (!toast || !toastContainer) return;

  // 阻止可能的重复调用
  if (toast.classList.contains("toast--hide")) return;

  // 清理定时器
  const timer = toastTimers.get(toast);
  if (timer) {
    clearTimeout(timer);
    toastTimers.delete(toast);
  }

  // 优化性能的动画处理
  toast.style.willChange = "opacity";
  toast.classList.remove("toast--show");
  toast.classList.add("toast--hide");

  // 使用一次性事件监听器减少内存占用
  toast.addEventListener("animationend", () => recycleToastElement(toast), { once: true });

  // 设置备用定时器，确保动画完成后回收
  setTimeout(() => {
    if (document.body.contains(toast)) {
      recycleToastElement(toast);
    }
  }, 300);
};

/**
 * 回收Toast元素到池中
 * @param {HTMLElement} toast - 要回收的Toast元素
 */
const recycleToastElement = (toast) => {
  if (!toast || !toastContainer) return;

  // 获取toast的位置
  const position = positionMap.get(toast) || "middle";
  const posContainer = getPositionContainer(position);

  if (!posContainer || !posContainer.contains(toast)) return;

  // 移除前先设置为不可见
  toast.style.display = "none";

  // 从DOM中移除
  posContainer.removeChild(toast);

  // 从活动列表中移除
  const index = activeToasts.indexOf(toast);
  if (index !== -1) {
    activeToasts.splice(index, 1);
  }

  // 减少该位置的toast计数
  if (positionCounts[position] > 0) {
    positionCounts[position]--;
  }

  // 更新其他toast的位置
  updateToastPositions(position);

  // 如果池未满，将元素添加到池中
  if (toastPool.length < MAX_POOL_SIZE) {
    // 确保清理定时器
    const timer = toastTimers.get(toast);
    if (timer) {
      clearTimeout(timer);
      toastTimers.delete(toast);
    }

    // 重置元素状态
    Object.assign(toast.style, {
      display: "",
      opacity: "0",
      willChange: "auto",
      marginTop: "",
      marginBottom: "",
      transform: "",
      zIndex: "",
    });

    // 清除所有动画相关状态
    toast.className = "toast--box";
    toast.style.animation = "none";

    // 强制浏览器重新计算
    void toast.offsetWidth;

    // 获取缓存的DOM元素引用
    const { messageElement, iconContainer } = getElementCache(toast);

    // 重置内容
    messageElement.textContent = "";
    iconContainer.style.display = "none";

    // 添加到池中
    toastPool.push(toast);
  }

  // 清除位置映射
  positionMap.delete(toast);
};

/**
 * 清除所有显示中的toast
 */
export const clearAllToasts = () => {
  if (!isBrowser || !toastContainer) return;

  // 清除所有防抖计时器
  Object.keys(debounceTimers).forEach((key) => {
    clearTimeout(debounceTimers[key]);
  });
  debounceTimers = {};

  // 清除所有toast定时器
  activeToasts.forEach((toast) => {
    const timer = toastTimers.get(toast);
    if (timer) {
      clearTimeout(timer);
      toastTimers.delete(toast);
    }
  });

  // 隐藏所有活动toast
  const toastsToHide = [...activeToasts];
  toastsToHide.forEach(hideToastElement);

  // 重置位置计数
  positionCounts.top = 0;
  positionCounts.middle = 0;
  positionCounts.bottom = 0;
};

/**
 * 生成防抖键
 * @param {string} content - 显示内容
 * @param {Object} options - 配置选项
 * @returns {string} - 防抖键
 */
const getDebounceKey = (content, options) => {
  return `${content}_${options.icon || "none"}_${options.position || "middle"}`;
};

/**
 * 显示Toast提示
 * @param {string} content - 显示的内容
 * @param {Object} options - 配置选项
 * @param {string} options.icon - 图标类型：success | warn
 * @param {number} options.duration - 显示持续时间，默认2000ms
 * @param {boolean} options.stack - 是否堆叠显示多个toast，默认true
 * @param {string} options.position - 显示位置：'top'|'middle'|'bottom'，默认'middle'
 * @param {boolean} options.debounce - 是否启用防抖，默认true
 * @returns {Object} - 包含清除方法的对象
 */
export const showToast = (content, options = {}) => {


  // 如果不在浏览器环境中，直接返回空方法
    if (!isBrowser || !content) return { clear: () => { } };
    if (String(content) ==='103') {
          content = 'IP地址已被封禁'
    }
    if (String(content) ==='107') {
         return 
    }
    if ( ['101','105','109'].includes(String(content))) {
        content = '登录已失效，请重新登录'
    }
  // 默认启用防抖和堆叠
  const enableDebounce = options.debounce !== false;
  const enableStack = options.stack !== false;

  if (enableDebounce) {
    const debounceKey = getDebounceKey(content, options);

    // 如果存在相同内容的toast正在防抖中，清除它
    if (debounceTimers[debounceKey]) {
      clearTimeout(debounceTimers[debounceKey]);
      debounceTimers[debounceKey] = null;
    }

    // 创建新的防抖计时器
    return new Promise((resolve) => {
      debounceTimers[debounceKey] = setTimeout(() => {
        delete debounceTimers[debounceKey];
        resolve(createAndShowToast(content, { ...options, stack: enableStack }));
      }, DEBOUNCE_TIME);
    });
  }

  return createAndShowToast(content, { ...options, stack: enableStack });
};

/**
 * 创建并显示Toast
 * @param {string} content - 显示内容
 * @param {Object} options - 配置选项
 * @returns {Object} - 包含清除方法的对象
 */
const createAndShowToast = (content, options) => {
  // 默认堆叠显示，除非明确设置为false
  if (!options.stack) {
    // 清除所有现有的toast
    activeToasts.slice().forEach(hideToastElement);
  }

  const toastElement = createToastElement(content, options.icon, options.position);
  if (!toastElement) return { clear: () => {} };

  activeToasts.push(toastElement);

  const duration = options.duration || 2000;

  // 设置定时器移除Toast并存储到映射中
  const timer = setTimeout(() => {
    hideToastElement(toastElement);
  }, duration);

  // 存储定时器引用
  toastTimers.set(toastElement, timer);

  // 返回清除方法
  return {
    clear: () => {
      const storedTimer = toastTimers.get(toastElement);
      if (storedTimer) {
        clearTimeout(storedTimer);
        toastTimers.delete(toastElement);
      }
      hideToastElement(toastElement);
    },
  };
};

// 初始化时应用样式
if (isBrowser && toastContainer) {
  requestAnimationFrame(fixToastStyles);
}
